import React from "react";
import { Icon, notify, confirmDialog } from "../registry";
import type { SiteBundle } from "./types";
import { OFFLINE_MSG } from "./fields";
import { fmtDateTime } from "./util";

/* ============================================================
   Website builder — call tracking (server/src/sites/calltracking.js,
   migration #40). "Track website calls": a local number shown on the site in
   place of the real one, ringing the business phone (an optional whisper,
   never recorded), so the calls the website brings in are counted — answered
   or missed — here, in the stats card, the weekly report and the ROI tile.
   Google keeps the real number (the site's schema.org markup and the Business
   Profile). Turning it off releases the number after a confirm.
   ============================================================ */

type CallRow = { id: string; from: string; status: "answered" | "missed"; duration: number; at: string; leadId: string | null };
type Sum = { days: number; total: number; answered: number; missed: number; talkMinutes: number };
export type CallsData = {
  setup: boolean; message?: string; configured: boolean; off?: boolean; price: number; phone: string;
  tracking: null | { id: string; status: "provisioning" | "active" | "released" | "error"; number: { display: string; e164: string } | null; areaCode: string; local: boolean; whisper: boolean; missedLeads: boolean; forwardTo: string; error: string | null; since: string | null };
  calls: CallRow[]; summary: null | { d7: Sum; d30: Sum }; sample?: boolean;
};

/* ---------------- pure helpers (web/scripts/unit-sitegrowth2.mjs) ---------------- */

/* 0 → "—", 75 → "1:15" */
export function talkTime(sec: number) {
  const s = Math.max(0, Math.round(Number(sec) || 0));
  if (!s) return "—";
  return Math.floor(s / 60) + ":" + String(s % 60).padStart(2, "0");
}
/* "about $1.15/mo" from the service's price */
export const priceText = (p: number) => `about $${(Number(p) || 1.15).toFixed(2)}/mo per number`;

/* believable sample numbers for the sandbox (no service) — deterministic */
export function sampleCalls(now: number): CallsData {
  const H = 3600000;
  const rows: CallRow[] = [
    { id: "c1", from: "(920) 555-0187", status: "answered", duration: 312, at: new Date(now - 3 * H).toISOString(), leadId: null },
    { id: "c2", from: "(920) 555-0133", status: "missed", duration: 0, at: new Date(now - 27 * H).toISOString(), leadId: "lead-webcall-1" },
    { id: "c3", from: "(414) 555-0162", status: "answered", duration: 95, at: new Date(now - 50 * H).toISOString(), leadId: null },
    { id: "c4", from: "(920) 555-0119", status: "answered", duration: 481, at: new Date(now - 4 * 24 * H).toISOString(), leadId: null },
  ];
  return {
    setup: true, configured: true, price: 1.15, phone: "(920) 555-0142", sample: true,
    tracking: { id: "n1", status: "active", number: { display: "(920) 555-9000", e164: "+19205559000" }, areaCode: "920", local: true, whisper: true, missedLeads: true, forwardTo: "(920) 555-0142", error: null, since: new Date(now - 20 * 24 * H).toISOString() },
    calls: rows,
    summary: { d7: { days: 7, total: 4, answered: 3, missed: 1, talkMinutes: 15 }, d30: { days: 30, total: 11, answered: 9, missed: 2, talkMinutes: 41 } },
  };
}

export function CallTrackingCard({ bundle, live, sample, go }: { bundle: SiteBundle; live: boolean; sample: boolean; go?: any }) {
  const DB: any = (window as any).VFP_DB;
  const [data, setData] = React.useState<CallsData | null>(() => (sample || !live ? sampleCalls(Date.now()) : null));
  const [err, setErr] = React.useState("");
  const [busy, setBusy] = React.useState("");
  React.useEffect(() => {
    if (sample || !live || !DB || !DB.siteCalls) return;
    DB.siteCalls().then((r: CallsData) => setData(r)).catch((e: any) => setErr(e.message || "Couldn't load call tracking."));
  }, [live, sample]);
  if (err && !data) return <div className="card pad ws-note">{err}</div>;
  if (!data) return <div className="card pad muted"><span className="ws-spin" style={{ marginRight: 8 }} />Loading call tracking…</div>;

  const t = data.tracking && (data.tracking.status === "active" || data.tracking.status === "provisioning") ? data.tracking : null;
  const failed = data.tracking && data.tracking.status === "error" ? data.tracking : null;
  const phone = data.phone || (bundle.site && bundle.site.draft && bundle.site.draft.business.phone.display) || "";
  const offline = () => { notify(sample ? "That's the sample site — on your real site this gets you a tracked number." : OFFLINE_MSG); };

  const enable = async () => {
    if (sample || !live) return offline();
    const ok = await confirmDialog({
      title: "Track calls from your website?",
      body: `We'll get a local number and show it on your website in place of ${phone}. Calls to it ring ${phone}. Your Google listing and your site's search markup keep ${phone}. The number costs about $${(Number(data.price) || 1.15).toFixed(2)} a month. Calls are never recorded.`,
      confirmLabel: "Get my number",
    });
    if (!ok) return;
    setBusy("enable");
    try { const r = await DB.siteCallsEnable({ whisper: true, missedLeads: true }); setData(r); notify(`Your website now shows ${r.tracking && r.tracking.number ? r.tracking.number.display : "your tracked number"} — it can take a minute to update.`, { kind: "success" }); }
    catch (e: any) { notify(e.message || "Couldn't get a number — try again.", { kind: "error" }); }
    setBusy("");
  };
  const disable = async () => {
    if (sample || !live) return offline();
    const num = t && t.number ? t.number.display : "the tracked number";
    const ok = await confirmDialog({
      title: "Turn off call tracking?",
      body: `Your website goes back to showing ${phone}. ${num} is released and stops working — anyone who saved it can't reach you through it. Your call log stays.`,
      confirmLabel: "Turn off and release it", danger: true,
    });
    if (!ok) return;
    setBusy("disable");
    try { const r = await DB.siteCallsDisable(); setData(r); notify("Call tracking is off. Your website shows your own number again."); }
    catch (e: any) { notify(e.message || "Couldn't turn it off — try again.", { kind: "error" }); }
    setBusy("");
  };
  const setting = async (k: "whisper" | "missedLeads", v: boolean) => {
    if (sample || !live) { setData({ ...data, tracking: data.tracking && { ...data.tracking, [k]: v } }); return; }
    setBusy(k);
    try { setData(await DB.siteCallsSettings({ [k]: v })); } catch (e: any) { notify(e.message || "Couldn't save that", { kind: "error" }); }
    setBusy("");
  };
  const sum = data.summary && data.summary.d30;

  return (
    <div className="card pad ct-card" data-call-tracking>
      <div className="row between" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <span className="kicker kicker-teal">Call tracking</span>
          <h3 className="display" style={{ fontSize: 18, margin: "6px 0 4px" }}>Calls from your website</h3>
        </div>
        {t ? <span className="chip green">{t.status === "provisioning" ? "Setting up…" : "On"}</span> : <span className="chip">Off</span>}
      </div>

      {data.setup === false && <p className="ws-note"><Icon name="lock" size={12} /> {data.message}</p>}

      {!t && data.setup !== false && (
        <>
          <p className="muted" style={{ fontSize: 13.5, margin: "2px 0 12px", maxWidth: 640 }}>
            Get a local number for your website that rings {phone || "your business phone"}, so you can see how many calls the site brings in — answered or missed. Google keeps your own number.
          </p>
          {failed && <div className="ws-err" style={{ marginBottom: 10 }}>Couldn't get a number last time{failed.error ? `: ${failed.error}` : ""}. Try again.</div>}
          <div className="ct-toggle">
            <button type="button" className="sw" role="switch" aria-checked={false} aria-label="Track website calls" disabled={!data.configured || !phone || busy === "enable"} onClick={enable}><span className="knob" /></button>
            <span><b>Track website calls</b><span className="faint">{busy === "enable" ? "Getting your number…" : `${priceText(data.price)}. Growth plan.`}</span></span>
          </div>
          {!data.configured && <p className="faint" style={{ fontSize: 12.5, margin: "8px 0 0" }}>{data.off ? "Call tracking is switched off on this server." : "Call tracking isn't set up on this server yet — Fence Works support turns it on."}</p>}
          {data.configured && !phone && <p className="faint" style={{ fontSize: 12.5, margin: "8px 0 0" }}>Add your business phone on the Business tab first — calls forward to it.</p>}
        </>
      )}

      {t && (
        <>
          <div className="ct-number">
            <div><span className="faint">Your website shows</span><b className="tnum">{t.number ? t.number.display : "…"}</b></div>
            <Icon name="arrow" size={16} />
            <div><span className="faint">It rings</span><b className="tnum">{t.forwardTo || phone}</b></div>
          </div>
          {!t.local && <p className="faint" style={{ fontSize: 12.5, margin: "0 0 8px" }}>No number was free in your area code, so this one is from another US area code.</p>}
          <div className="ct-settings">
            <label className="ct-toggle">
              <button type="button" className={"sw" + (t.whisper ? " on" : "")} role="switch" aria-checked={t.whisper} aria-label="Whisper before connecting" disabled={busy === "whisper"} onClick={() => setting("whisper", !t.whisper)}><span className="knob" /></button>
              <span><b>Say “Call from your website” before I pick up</b><span className="faint">Only you hear it, so you know how they found you.</span></span>
            </label>
            <label className="ct-toggle">
              <button type="button" className={"sw" + (t.missedLeads ? " on" : "")} role="switch" aria-checked={t.missedLeads} aria-label="Add missed calls to Leads" disabled={busy === "missedLeads"} onClick={() => setting("missedLeads", !t.missedLeads)}><span className="knob" /></button>
              <span><b>Add missed calls to Leads</b><span className="faint">As “Missed website call”, so nobody slips through.</span></span>
            </label>
            <p className="faint" style={{ fontSize: 12.5, margin: 0 }}><Icon name="lock" size={12} /> Calls are never recorded. Google and your site's search markup keep {phone}.</p>
          </div>
        </>
      )}

      {(t || (data.calls || []).length > 0) && (
        <>
          {sum && (
            <div className="wg-tiles wg-tiles-3 ct-tiles">
              <div className="wg-tile"><span>Calls</span><b className="tnum">{sum.total}</b><em>last 30 days</em></div>
              <div className="wg-tile"><span>Answered</span><b className="tnum">{sum.answered}</b><em>{sum.talkMinutes ? `${sum.talkMinutes} min talked` : ""}</em></div>
              <div className="wg-tile"><span>Missed</span><b className="tnum" style={{ color: sum.missed ? "var(--amber)" : undefined }}>{sum.missed}</b><em>{sum.missed && t && t.missedLeads ? "in your Leads" : ""}</em></div>
            </div>
          )}
          <div className="wg-sub" style={{ marginTop: 12 }}>Call log</div>
          {!(data.calls || []).length ? <p className="muted" style={{ fontSize: 13, margin: 0 }}>No calls yet. They show up here the moment one comes in.</p> : (
            <div className="ct-log">
              {data.calls.slice(0, 20).map((c) => (
                <div key={c.id} className="ct-call" data-status={c.status}>
                  <span className="ct-ic"><Icon name="phone" size={13} /></span>
                  <span style={{ minWidth: 0, flex: 1 }}><b className="tnum">{c.from}</b><span className="faint"> · {fmtDateTime(c.at)}</span></span>
                  {c.status === "answered" ? <span className="chip green">Answered · {talkTime(c.duration)}</span> : <span className="chip amber">Missed</span>}
                  {c.leadId ? <span className="faint ct-lead">lead added</span> : null}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {t && (
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" className="rv-link" style={{ color: "var(--red)" }} disabled={busy === "disable"} onClick={disable}>{busy === "disable" ? "Turning off…" : "Turn off call tracking"}</button>
        </div>
      )}
    </div>
  );
}
